import React, { Fragment, useEffect, useState, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { Row, Col, Card, CardBody } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../../components/Loaders/DnaLoader";
import "../../../assets/custom css/loadoverlay.scss";
import DriverLocationService from "./driverLocation.service";
import AlertService from "../../../components/Alerts/alerts";

const DriverTrackingHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { driverId } = useParams();
  const [loading, setLoading] = useState(false);
  const [driverName, setDriverName] = useState("");
  const [locations, setLocations] = useState([]);

  const fetchDriverHistory = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get driver location history
      const response = await DriverLocationService.getDriverLocation({
        driver_id: driverId,
        limit: 1000, // Get last 1000 locations
        offset: 0,
        sort: { timestamp: -1 } // Latest first
      });

      // Handle unauthorized response
      if (response.data && response.data.message === "Unauthorized") {
        // Handle logout if needed
        navigate("/login");
        return;
      }

      if (response.data && response.data.status) {
        const locationsData = response.data.data || [];
        setLocations(locationsData);
        
        // Get driver name from the first location or fetch from API
        if (locationsData && locationsData.length > 0) {
          // Try to get driver name from active drivers API
          try {
            const activeDriversResponse = await DriverLocationService.getActiveDriversLocations({});
            if (activeDriversResponse.data && activeDriversResponse.data.status && activeDriversResponse.data.data) {
              const driverInfo = activeDriversResponse.data.data.find(
                d => d.driver_id === driverId
              );
              if (driverInfo && driverInfo.driver_name) {
                setDriverName(driverInfo.driver_name);
              } else {
                setDriverName(`Driver ${driverId.substring(0, 12)}...`);
              }
            } else {
              setDriverName(`Driver ${driverId.substring(0, 12)}...`);
            }
          } catch (error) {
            console.error("Error fetching driver name:", error);
            setDriverName(`Driver ${driverId.substring(0, 12)}...`);
          }
        }
        
        if (locationsData.length === 0) {
          AlertService.swalErrorAlert("No location history found for this driver");
        }
      } else {
        AlertService.swalErrorAlert(response.data?.message || "Failed to fetch driver history");
      }
    } catch (error) {
      console.error("Error fetching driver history:", error);
      AlertService.swalErrorAlert("Error loading driver tracking history");
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  // Fetch driver tracking history
  useEffect(() => {
    if (driverId) {
      fetchDriverHistory();
    }
  }, [driverId, fetchDriverHistory]);

  // Helper function to open location in Google Maps
  const openInGoogleMaps = (latitude, longitude) => {
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(url, '_blank');
  };

  return (
    <Fragment>
      <div className="app-main__outer">
        <div className="app-main__inner">
          <div className="bg-light-gray bg-animation">
            <div className="d-flex justify-content-center align-items-center">
              <Col md="12" className="mx-auto app-login-box">
                <div className="app-page-title mb-1">
                  <div className="page-title-wrapper">
                    <div className="page-title-heading fw-bold">
                      <div className="page-title-icon">
                        <i className="pe-7s-map-marker icon-gradient bg-happy-green" />
                      </div>
                      <div>
                        Driver Tracking History
                        <div className="page-title-subheading">
                          {driverName || 'View location history and tracking route'}
                        </div>
                      </div>
                    </div>
                    <div className="page-title-actions">
                      <button 
                        type="button" 
                        onClick={() => navigate('/driver-locations-map')} 
                        className="btn btn-outline-success btn-hover-shine me-3"
                      >
                        Back to Map
                      </button>
                    </div>
                  </div>
                </div>

                <LoadingOverlay 
                  tag="div" 
                  active={loading}
                  styles={{ overlay: (base) => ({ ...base }) }}
                  spinner={<DNALoader />}
                >
                  {locations.length > 0 && (
                    <Row>
                      <Col md="12">
                        <Card className="main-card mb-3">
                          <CardBody>
                            <h5 className="card-title mb-3">Location History ({locations.length} records)</h5>
                            <div className="table-responsive">
                            <table className="table table-hover">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Timestamp</th>
                                  <th>Latitude</th>
                                  <th>Longitude</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {locations.map((location, index) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                      {location.timestamp || location.datetime_added
                                        ? new Date(location.timestamp || location.datetime_added).toLocaleString()
                                        : 'N/A'}
                                    </td>
                                    <td>
                                      {location.latitude !== undefined && location.latitude !== null && !isNaN(location.latitude)
                                        ? location.latitude.toFixed(6)
                                        : 'N/A'}
                                    </td>
                                    <td>
                                      {location.longitude !== undefined && location.longitude !== null && !isNaN(location.longitude)
                                        ? location.longitude.toFixed(6)
                                        : 'N/A'}
                                    </td>
                                    <td>
                                      {location.latitude !== undefined && location.latitude !== null && 
                                       location.longitude !== undefined && location.longitude !== null &&
                                       !isNaN(location.latitude) && !isNaN(location.longitude) ? (
                                        <button
                                          className="btn btn-sm btn-primary"
                                          onClick={() => openInGoogleMaps(location.latitude, location.longitude)}
                                        >
                                          View on Google Maps
                                        </button>
                                      ) : (
                                        'N/A'
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  )}
                </LoadingOverlay>
              </Col>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default DriverTrackingHistory;

